import { Outlet, NavLink, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  House, Users as UsersIcon, Student, ClipboardText, CalendarCheck,
  BookOpen, ChartLine, ChatCircleDots, SignOut, GraduationCap, Globe,
} from "@phosphor-icons/react";
import { isMaster, schoolName } from "./SchoolSelect";

const nav = [
  { to: "/dashboard", label: "Dashboard", icon: House, roles: ["admin","principal","teacher","parent","staff","accounts"] },
  { to: "/attendance", label: "Attendance", icon: CalendarCheck, roles: ["admin","principal","teacher"] },
  { to: "/lesson-plans", label: "Lesson Plans", icon: BookOpen, roles: ["admin","principal","teacher"] },
  { to: "/tasks", label: "Daily Tasks", icon: ClipboardText, roles: ["admin","principal","teacher","staff","accounts"] },
  { to: "/exams", label: "Exams & Marks", icon: ChartLine, roles: ["admin","principal","teacher","parent"] },
  { to: "/performance", label: "Student Performance", icon: Student, roles: ["admin","principal","teacher","parent"] },
  { to: "/communications", label: "Parent Comms", icon: ChatCircleDots, roles: ["admin","principal","teacher","parent"] },
  { to: "/students", label: "Students", icon: Student, roles: ["admin","principal","teacher","parent"] },
  { to: "/users", label: "People / Staff", icon: UsersIcon, roles: ["admin","principal"] },
  { to: "/website", label: "Website Content", icon: Globe, roles: ["admin"], master: true },
];

const roleBadge = {
  admin: "bg-indigo-100 text-indigo-800",
  principal: "bg-emerald-100 text-emerald-800",
  teacher: "bg-sky-100 text-sky-800",
  parent: "bg-amber-100 text-amber-800",
  staff: "bg-zinc-200 text-zinc-800",
  accounts: "bg-rose-100 text-rose-800",
};

export default function Layout() {
  const { user, logout } = useAuth();
  const loc = useLocation();
  if (!user) return null;
  const links = nav.filter((n) => n.roles.includes(user.role) && (!n.master || isMaster(user)));

  return (
    <div className="min-h-screen flex bg-zinc-50">
      {/* Sidebar */}
      <aside className="w-64 border-r border-zinc-200 bg-white flex flex-col" data-testid="sidebar">
        <div className="h-16 flex items-center gap-2 px-5 border-b border-zinc-200">
          <div className="h-9 w-9 rounded-md bg-indigo-700 grid place-items-center text-white">
            <GraduationCap size={20} weight="bold" />
          </div>
          <div>
            <div className="font-display font-bold tracking-tight">Scholaris</div>
            <div className="text-[11px] text-zinc-500 -mt-0.5">AY 2026–27</div>
          </div>
        </div>
        <div className="px-5 py-2 border-b border-zinc-200 text-xs font-semibold text-indigo-800 bg-indigo-50" data-testid="sidebar-school-name">
          {isMaster(user) ? "Master · Both schools" : schoolName(user.school_id)}
        </div>
        <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
          {links.map((n) => {
            const Icon = n.icon;
            const active = loc.pathname === n.to;
            return (
              <NavLink
                key={n.to} to={n.to}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                  active
                    ? "bg-indigo-50 text-indigo-800 font-semibold"
                    : "text-zinc-700 hover:bg-zinc-100"
                }`}
              >
                <Icon size={18} weight={active ? "fill" : "regular"} />
                {n.label}
              </NavLink>
            );
          })}
        </nav>
        <div className="p-3 border-t border-zinc-200">
          <div className="flex items-center gap-3 px-2 py-2">
            <div className="h-9 w-9 rounded-full bg-zinc-900 text-white grid place-items-center font-semibold text-sm">
              {user.name.charAt(0)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold truncate">{user.name}</div>
              <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold uppercase tracking-wide ${roleBadge[user.role]}`}>
                {user.role}
              </span>
            </div>
            <button
              data-testid="logout-button"
              className="text-zinc-500 hover:text-rose-600 transition-colors p-1"
              onClick={logout} title="Sign out">
              <SignOut size={18} />
            </button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 overflow-x-hidden">
        <div className="sticky top-0 z-10 h-14 border-b border-zinc-200 bg-white/70 backdrop-blur-xl px-6 flex items-center justify-between">
          <div className="font-display text-sm font-bold tracking-tight uppercase text-zinc-500">
            School Management Portal
          </div>
          <div className="text-xs text-zinc-500">
            {new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" })}
          </div>
        </div>
        <div className="p-6 md:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
